import React from "react"
import { Editor } from "@tiptap/react"
import QueryStatus from "features/QueryConsole/components/QueryStatus.component"
import QueryConsoleActions from "features/QueryConsole/components/QueryConsoleActions.component"
import QueryConsoleViewMode from "features/QueryConsole/components/QueryConsoleViewMode.component"
import QueryConsoleLayoutControls from "features/QueryConsole/components/QueryConsoleLayoutControls.component"
import QueryConsoleHelp from "features/QueryConsole/components/QueryConsoleHelp.component"
import { useQueryConsoleLayoutStore } from "stores/queryConsoleLayoutStore"
import { QueryInstance } from "types/engine.types"

interface QueryConsoleToolbarProps {
  /** Saved query names of the project, for the saved-query picker. */
  projectQueries: string[]
  /** The script loaded in the Console editor. */
  selectedQuery: string
  setSelectedQuery: (query: string) => void
  editor: Editor | null
  contentChanged: boolean
  setContentChanged: (changed: boolean) => void
  queryInstance: QueryInstance | null
  runScript: () => void
  isRunningScript: boolean
  /** Whether the current view has a runnable script (Builder needs row+col vars). */
  canRun: boolean
  saveProjectQuery: (queryName: string) => void
  activeKey?: string
  /** The editor shows a pipeline/builder script: Save goes to "Save as". */
  forceSaveAs: boolean
  /** Whether the project has a Run on load list. */
  runOnLoadOn: boolean
  onOpenRunOnLoad: () => void
  onOpenLineage: () => void
  onOpenVersionCheck: () => void
  /** Shown first on the right (the assistant's toggle). */
  headerActions?: React.ReactNode
}

/**
 * The Data Reporting action row, one line that never wraps.
 *
 * Left: the view mode (Builder, Transform, Console, Saved queries), the
 * saved-query picker and help. Right: "Layout ▾" and full screen, "Tools ▾",
 * "Save ▾" and Run, the one solid button.
 */
const QueryConsoleToolbar: React.FC<QueryConsoleToolbarProps> = ({
  projectQueries,
  selectedQuery,
  setSelectedQuery,
  editor,
  contentChanged,
  setContentChanged,
  queryInstance,
  runScript,
  isRunningScript,
  canRun,
  saveProjectQuery,
  activeKey,
  forceSaveAs,
  runOnLoadOn,
  onOpenRunOnLoad,
  onOpenLineage,
  onOpenVersionCheck,
  headerActions,
}) => {
  const viewMode = useQueryConsoleLayoutStore((s) => s.viewMode)

  return (
    <div className="qc-action-row flex flex-row flex-nowrap items-center p-2 gap-2 min-w-0">
      {/* Below about 720px the left group gives way: the picker shrinks, then
          the group clips rather than running under the right-hand menus. */}
      <div className="flex min-w-0 items-center gap-2 overflow-hidden">
        <QueryConsoleViewMode />
        <QueryStatus
          projectQueries={projectQueries}
          selectedQuery={selectedQuery}
          editor={editor}
          setContentChanged={setContentChanged}
          setSelectedQuery={setSelectedQuery}
        />
        <QueryConsoleHelp />
      </div>
      <div className="flex flex-none ml-auto items-center gap-2">
        {headerActions}
        <QueryConsoleLayoutControls />
        <QueryConsoleActions
          queryInstance={queryInstance}
          runScript={runScript}
          isRunningScript={isRunningScript}
          canRun={canRun}
          saveProjectQuery={saveProjectQuery}
          selectedQuery={selectedQuery}
          setSelectedQuery={setSelectedQuery}
          contentChanged={contentChanged}
          activeKey={activeKey}
          // Transform has its own Apply (in the Transform header); Saved
          // is a list view with no editor, so Run means nothing in either.
          hideRun={viewMode === "transform" || viewMode === "saved"}
          // Builder + Transform have kind-aware Save in their own
          // toolbars; the action-row Save is only useful for Console.
          hideSave={viewMode !== "console"}
          // When the editor is showing a structured record's script,
          // force Save to "Save as" so the original pipeline/builder
          // sidecar isn't clobbered with a script-kind one.
          forceSaveAs={forceSaveAs}
          runOnLoadOn={runOnLoadOn}
          onOpenRunOnLoad={onOpenRunOnLoad}
          onOpenLineage={onOpenLineage}
          onOpenVersionCheck={onOpenVersionCheck}
          // Format script works on the editor, which only Console shows.
          onFormatScript={
            viewMode === "console" && editor
              ? () => editor.commands.formatQueryScript()
              : undefined
          }
        />
      </div>
    </div>
  )
}

export default QueryConsoleToolbar
